import { Controller } from '@hotwired/stimulus';

/**
 * Tag Combobox Controller (create-or-pick, body-portaled dropdown)
 *
 * Replaces the CR « remarque » tag `<select>` of Label ids (which could only PICK
 * from a dictionary that nothing ever populated) with a filter-as-you-type input +
 * dropdown that BOTH reuses an existing chantier tag AND creates a brand-new one
 * ad-hoc.
 *
 * Two modes, discriminated by `createUrlValue`:
 *   - EDIT / post mode (`createUrlValue` present) — choosing an option immediately
 *     POSTs `tag[label_id]` (reuse) OR `tag[name]` (create) and renders the returned
 *     Turbo Stream (the server replaces the `remark-tags-<id>` block).
 *   - CREATE / stage mode (`createUrlValue` blank) — the remarque does not exist yet,
 *     so choosing an option STAGES a hidden `observation[tag_names][]` input + a chip
 *     into the main create form (mirror of the photos `observation[photos][]` path);
 *     `RemarksController#create` find-or-creates a Label per name after the remarque
 *     is built. Staged chips are removable client-side (unstage) before submit.
 *
 * The dropdown is rendered client-side from `labelsValue` (the chantier's existing
 * kept labels): a « Réutiliser » group of matches + a « Créer "X" » option when the
 * query does not match an existing label exactly.
 *
 * DROPDOWN PORTAL (overflow-clip fix). The `list` dropdown is built inside the
 * remark drawer, whose Tags accordion body carries a PERMANENT `overflow-hidden`
 * (required by the `grid-template-rows` disclosure animation) AND whose form body
 * is `flex-1 overflow-y-auto` — an absolutely-positioned dropdown was CLIPPED at
 * the section/scroll edge. So on connect the `list` element is REPARENTED to
 * `document.body` (`position: fixed`, `z-[60]` — above the z-50 slide-over, the
 * codebase's established "above the drawer" convention) and positioned under the
 * input via `getBoundingClientRect()`, mirroring `dropdown_controller.js`'s
 * escape-clipping portal. Because the portaled list lives OUTSIDE this controller's
 * Stimulus scope, its item clicks are wired with a DELEGATED JS listener (a
 * `data-action` on a body node never dispatches — the list is built in JS anyway,
 * so wiring the handler in JS is natural). It is repositioned on scroll/resize
 * while open and torn down on `disconnect()` (no DOM leak when the drawer closes).
 *
 * ERROR HANDLING (post/edit mode, F5). `#choose` is async and only commits UI
 * state — clearing the input, closing the dropdown — on a CONFIRMED write.
 * `#post` awaits the fetch and returns a boolean gated on BOTH `response.ok` AND
 * the response content-type being `text/vnd.turbo-stream.html` (content-type alone
 * is not enough: the server's error path answers `head :unprocessable_content`
 * with no body, but a future 422-with-turbo-stream-body must not be misread as
 * success). On failure the dropdown CLOSES (it is body-portaled, `position: fixed`,
 * and `#position()` parks it directly under the input — exactly where the status
 * line sits — so leaving it open hides the error completely; QA-confirmed) while
 * the typed query STAYS in the input, and the `status` target (a
 * `role="status" aria-live="polite"` node — always present, empty by default) is
 * set to "Ajout du tag impossible. Réessayez." so a rejected write (expired
 * session, server guard, network drop) is never silently indistinguishable from
 * success. Re-focusing the input re-opens the dropdown (`#renderList` clears the
 * status) for an immediate retry. Stage mode has no network path and stays
 * synchronous.
 *
 * Targets:
 *   - input        : the text field.
 *   - list         : the dropdown container (reparented to body + built by #renderList).
 *   - staged       : (stage mode) container for staged hidden inputs + chips.
 *   - chipTemplate  : (stage mode) <template> cloned per staged tag.
 *   - status       : (post/edit mode) aria-live error region — set on a rejected
 *                    POST, cleared on a confirmed choose / re-filter / re-open.
 *
 * Values:
 *   - labels    : [{ id, name }] — the chantier's existing kept labels.
 *   - createUrl : the POST endpoint (edit mode) or "" (create/stage mode).
 *
 * Actions:
 *   - filter   : `input` — rebuild + open the dropdown.
 *   - open     : `focus` — open the dropdown.
 *   - onBlur   : `blur` — close after a short delay (lets a portaled item click land).
 *   - onKeydown: `keydown` — Enter picks the exact match or creates; Escape closes.
 *   - unstage  : `click` on a staged chip ✕ (stage mode).
 *
 * (Dropdown item clicks are wired via the delegated `#onListClick`, NOT a Stimulus
 * action — the list is portaled out of scope.)
 */
export default class extends Controller {
  static targets = ['input', 'list', 'staged', 'chipTemplate', 'status'];
  static values = { labels: Array, createUrl: String };

  connect() {
    // Portal the dropdown to <body> so no ancestor overflow / scroll clips it.
    // Keep a direct ref: once reparented, Stimulus can't resolve `listTarget`.
    this.listEl = this.hasListTarget ? this.listTarget : null;
    if (this.listEl) document.body.appendChild(this.listEl);

    this.onDocPointer = (event) => {
      if (this.element.contains(event.target)) return;
      if (this.listEl && this.listEl.contains(event.target)) return;
      this.#close();
    };
    document.addEventListener('click', this.onDocPointer);

    // The list is portaled out of scope → wire its item clicks in JS directly.
    this.onListClick = (event) => {
      const option = event.target.closest('button[data-name]');
      if (!option) return;
      event.preventDefault();
      this.#choose(option.dataset.name, option.dataset.labelId || null);
    };
    this.listEl?.addEventListener('click', this.onListClick);

    // Keep the dropdown pinned under the input while the drawer body scrolls.
    this.onReposition = () => {
      if (this.#isOpen()) this.#position();
    };
    window.addEventListener('scroll', this.onReposition, { capture: true, passive: true });
    window.addEventListener('resize', this.onReposition);
  }

  disconnect() {
    document.removeEventListener('click', this.onDocPointer);
    window.removeEventListener('scroll', this.onReposition, { capture: true });
    window.removeEventListener('resize', this.onReposition);
    this.listEl?.removeEventListener('click', this.onListClick);
    // Remove the portaled node so it can't leak when the drawer is torn down.
    this.listEl?.remove();
    clearTimeout(this.blurTimer);
  }

  get stageMode() {
    return !this.createUrlValue;
  }

  filter() {
    this.#renderList();
  }

  open() {
    this.#renderList();
  }

  // Close on blur, but deferred: clicking a portaled item blurs the input first,
  // so an immediate close would tear the list down before the item click lands.
  onBlur() {
    clearTimeout(this.blurTimer);
    this.blurTimer = setTimeout(() => this.#close(), 150);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.#close();
      return;
    }
    if (event.key !== 'Enter') return;

    // Never let Enter submit the surrounding remarque <form>.
    event.preventDefault();
    const query = this.inputTarget.value.trim();
    if (!query) return;

    const existing = this.#existingLabel(query);
    if (existing) {
      this.#choose(existing.name, String(existing.id));
    } else {
      this.#choose(query, null);
    }
  }

  unstage(event) {
    event.preventDefault();
    event.currentTarget.closest('[data-tag-combobox-staged-chip]')?.remove();
  }

  // ── internals ───────────────────────────────────────────────────────────────

  async #choose(name, labelId) {
    // Cancel the deferred blur-close NOW, regardless of the async outcome below.
    // onBlur() arms a 150ms timer (clicking a portaled option blurs the input
    // first); the old synchronous #close() used to cancel it as a side effect.
    // Now that the close is deferred behind an await, a #post slower than 150ms
    // would let the stale timer fire #close() mid-flight and tear the dropdown
    // down out from under the pending request.
    clearTimeout(this.blurTimer);
    this.#clearStatus();

    // Re-entrancy guard: both call sites (onKeydown's Enter and the delegated
    // onListClick) invoke #choose without awaiting — a fast double Enter/click
    // must not fire two overlapping POSTs.
    if (this.pending) return;

    if (this.stageMode) {
      // No network path here — cannot fail, so it stays synchronous.
      this.#stage(name);
      this.inputTarget.value = '';
      this.#close();
      return;
    }

    this.pending = true;
    this.inputTarget.disabled = true;
    try {
      const ok = await this.#post(name, labelId);
      if (!ok) {
        // Close the dropdown so the status line below the input is actually VISIBLE: the
        // list is body-portaled (position: fixed, z-[60]) and #position() parks it directly
        // under the input — exactly where the status <p> sits — so leaving it open hides the
        // error completely (QA-confirmed). The typed query STAYS in the input, so re-focusing
        // re-opens the list (#renderList clears the status) for an immediate retry.
        this.#close();
        this.#setStatus('Ajout du tag impossible. Réessayez.');
        return;
      }
      this.inputTarget.value = '';
      this.#close();
    } finally {
      this.pending = false;
      this.inputTarget.disabled = false;
    }
  }

  #stage(name) {
    const trimmed = name.trim();
    if (!trimmed || !this.hasChipTemplateTarget || !this.hasStagedTarget) return;
    // De-dup by case-insensitive name against the already-staged inputs.
    const existing = this.#stagedNames().some((n) => n.toLowerCase() === trimmed.toLowerCase());
    if (existing) return;

    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    const label = chip.querySelector('[data-tag-combobox-chip-label]');
    const input = chip.querySelector('input[type="hidden"]');
    const removeBtn = chip.querySelector('button');
    if (label) label.textContent = trimmed;
    if (input) {
      input.name = 'observation[tag_names][]';
      input.value = trimmed;
    }
    if (removeBtn) removeBtn.setAttribute('aria-label', `Retirer le tag ${trimmed}`);
    this.stagedTarget.appendChild(chip);
  }

  // Returns a boolean success, gated on BOTH the status code AND the
  // content-type — content-type alone is not enough: the server's error path
  // answers `head :unprocessable_content` (no body), but if any future path ever
  // returns a 422 WITH a turbo-stream body, a content-type-only check would
  // render it and read the failure as success.
  async #post(name, labelId) {
    const body = new URLSearchParams();
    if (labelId) {
      body.append('tag[label_id]', labelId);
    } else {
      body.append('tag[name]', name);
    }

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.#csrfToken(),
        },
      });

      const contentType = response.headers.get('Content-Type') || '';
      const ok = response.ok && contentType.includes('text/vnd.turbo-stream.html');

      if (!ok) {
        console.error(`[tag-combobox] POST failed: HTTP ${response.status} (${contentType})`);
        return false;
      }

      window.Turbo?.renderStreamMessage?.(await response.text());
      return true;
    } catch (error) {
      console.error('[tag-combobox] POST failed:', error);
      return false;
    }
  }

  #renderList() {
    clearTimeout(this.blurTimer);
    // Covers both entry points (filter() and open()): a stale error from a
    // previous failed POST must not survive a re-filter or the disclosure being
    // closed and reopened (no Stimulus disconnect fires for a mere hidden/inert
    // toggle, so the message would otherwise persist).
    this.#clearStatus();
    if (!this.listEl) return;

    const query = this.inputTarget.value.trim();
    const matches = this.#matchingLabels(query);
    const exact = query ? this.#existingLabel(query) : null;

    const parts = [];

    if (matches.length) {
      parts.push(this.#groupHeader('Réutiliser'));
      matches.forEach((label) => parts.push(this.#reuseOption(label)));
    }

    if (query && !exact) {
      if (parts.length) parts.push(this.#divider());
      parts.push(this.#createOption(query));
    }

    if (!parts.length) {
      this.#close();
      return;
    }

    this.listEl.replaceChildren(...parts);
    this.listEl.classList.remove('hidden');
    this.#position();
  }

  #close() {
    clearTimeout(this.blurTimer);
    if (this.listEl) {
      this.listEl.classList.add('hidden');
      this.listEl.replaceChildren();
    }
  }

  #isOpen() {
    return !!this.listEl && !this.listEl.classList.contains('hidden');
  }

  #setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message;
  }

  #clearStatus() {
    if (this.hasStatusTarget) this.statusTarget.textContent = '';
  }

  // Position the body-portaled dropdown under the input (flip above when there is
  // no room below), width-matched to the input, clamped to the viewport. Calqued
  // on dropdown_controller.js#positionPortal.
  #position() {
    if (!this.listEl) return;

    const rect = this.inputTarget.getBoundingClientRect();
    const gap = 4;
    const margin = 8;

    this.listEl.style.width = `${rect.width}px`;

    // Measure now — #renderList already un-hid the list, so offsetHeight is real.
    const height = this.listEl.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    let top;
    if (spaceBelow >= height || spaceBelow >= spaceAbove) {
      top = rect.bottom + gap;
    } else {
      top = rect.top - height - gap;
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));

    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin));

    this.listEl.style.top = `${top}px`;
    this.listEl.style.left = `${left}px`;
  }

  #matchingLabels(query) {
    const q = query.toLowerCase();
    return this.labelsValue.filter((label) => !q || label.name.toLowerCase().includes(q));
  }

  #existingLabel(query) {
    const q = query.toLowerCase();
    return this.labelsValue.find((label) => label.name.toLowerCase() === q);
  }

  #stagedNames() {
    if (!this.hasStagedTarget) return [];
    return Array.from(this.stagedTarget.querySelectorAll('input[type="hidden"]')).map(
      (i) => i.value
    );
  }

  #groupHeader(text) {
    const p = document.createElement('p');
    p.className =
      'px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-neutral-400';
    p.textContent = text;
    return p;
  }

  #divider() {
    const div = document.createElement('div');
    div.className = 'border-t border-neutral-100';
    return div;
  }

  #reuseOption(label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none';
    button.dataset.name = label.name;
    button.dataset.labelId = String(label.id);
    const dot = document.createElement('span');
    dot.className = 'h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400';
    const text = document.createElement('span');
    text.textContent = label.name;
    button.append(dot, text);
    return button;
  }

  #createOption(query) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-primary-700 hover:bg-primary-50 focus-visible:bg-primary-50 focus-visible:outline-none';
    button.dataset.name = query;
    button.textContent = `Créer « ${query} »`;
    return button;
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }
}
